/* My recorded meetings (§11): the summary of every meeting I took part in,
   as a personal, durable feed.

   The readout in the room is the shared copy — and it gets buried in busy
   rooms or vanishes with temporary ones. This store is the per-participant
   copy: server-backed (the memory layer's DB is the source of truth, so it
   works for rooms I can no longer read), with only a "seen up to" watermark
   in account data driving the rail/tab unread badge. The app.atrium.readout
   event every participant receives live is the refresh trigger + toast. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { sync } from '@/lib/sync'
import { memoryApi, type MeetingListItem } from '@/lib/memoryApi'
import { usePresence } from '@/stores/presence'
import { useToasts } from '@/stores/toasts'
import { playSfx } from '@/lib/sfx'

const STORE = 'app.atrium.meetings'

/** When a meeting entered the feed: processing finishes after the call ends,
    so processed_at is the honest "new since" clock (ended_at is the fallback
    for rows predating it). */
function freshTs(m: MeetingListItem): number {
  return m.processed_at || m.ended_at
}

export const useMeetings = defineStore('meetings', {
  state: () => ({
    items: [] as MeetingListItem[],
    /** Watermark (ms): summaries at/under this are seen; newer ones badge. */
    seen: 0,
    inited: false,
    loaded: false,
    refreshTimer: null as ReturnType<typeof setTimeout> | null,
  }),

  getters: {
    unread: (s): number => s.items.reduce((n, m) => n + (freshTs(m) > s.seen ? 1 : 0), 0),
  },

  actions: {
    init() {
      if (this.inited) return
      this.inited = true
      // A readout landing in a room I sync means a meeting finished processing
      // — refresh my feed (debounced; the list endpoint decides what's mine).
      sync.on('custom', (_roomId, ev) => {
        if (ev.type !== 'app.atrium.readout') return
        if ((ev.content as any)?.kind !== 'meeting_summary') return
        this.scheduleRefresh()
      })
    },

    /** Boot: load the watermark, then my feed. First run starts the clock now
        — pre-feature history must not light the badge. */
    async boot() {
      this.init()
      if (this.loaded) return
      try {
        const data = await matrix.getAccountData<{ seen?: number }>(STORE)
        if (typeof data?.seen === 'number') this.seen = data.seen
      } catch { /* none yet */ }
      if (!this.seen) {
        this.seen = Date.now()
        this.persistSeen()
      }
      await this.refresh(false)
      this.loaded = true
    },

    scheduleRefresh() {
      if (this.refreshTimer) return
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null
        void this.refresh(true)
      }, 2000)
    },

    async refresh(live: boolean) {
      try {
        const before = new Set(this.items.map((m) => m.id))
        const { meetings } = await memoryApi.meetings()
        this.items = meetings
        if (live && this.loaded) {
          const fresh = meetings.filter((m) => !before.has(m.id))
          if (fresh.length && !usePresence().isDnd) {
            playSfx('mention')
            useToasts().push({
              icon: 'waveform', color: 'var(--gold-bright)',
              title: 'Meeting summary ready',
              text: fresh.length > 1
                ? `${fresh.length} of your meetings finished processing.`
                : `“${fresh[0].room_name || 'Your meeting'}” has been summarized.`,
              action: 'Read', ms: 7000,
              onAction: () => window.location.assign('#/mentions?tab=meetings'),
            })
          }
        }
      } catch {
        /* memory layer down — keep what we have */
      }
    },

    /** Viewing the tab moves the watermark to the newest item (badge only —
        the items themselves stay, that's the point). */
    markSeen() {
      const newest = this.items.reduce((n, m) => Math.max(n, freshTs(m)), 0)
      if (newest > this.seen) {
        this.seen = newest
        this.persistSeen()
      }
    },

    persistSeen() {
      void matrix.setAccountData(STORE, { seen: this.seen }).catch(() => {})
    },
  },
})
